// src/pages/LandingHero.jsx
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import "./landing-hero.css";
import AcademyShowcase from "./academyShowcase";
import InkapprenticeTV from "./inkApprenticeTV";
import AcademyFeatures from "./AcademyFeatures";
import AcademyCommunitySection from "./AcademyCommunitySection";
import ReviewsSection from "./ReviewSection";

/* ===================== Helpers ===================== */
function lerp(a, b, t) { return a + (b - a) * t; }
function easeInOutCubic(t){ return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
const fmtUSD = (n) => n.toLocaleString(undefined, { maximumFractionDigits: 0 });

/* Viewport progress for a section (0..1 across its scroll) */
function useSectionScrollProgress(ref){
  const [p, setP] = useState(0);
  useEffect(() => {
    function onScroll(){
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;

      // ~0 just before visible, ~1 after scrolled past
      const raw = (vh - r.top) / (vh + r.height);
      const clamped = Math.min(1, Math.max(0, raw));
      setP(clamped);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive:true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);
  return p;
}
// at the top of the file (near other asset helpers)
const MACHINE_ICON = "https://cosacademyassets.s3.us-east-2.amazonaws.com/machineDrop.png"; 
// ^ swap to your exact file path/name; PNG/WebP both fine

/* ===================== Scroll Earnings (centered + luxe) ===================== */
function EarningsScrollSection({
  // endpoints (tweak to taste)
  priceMin = 300, priceMax = 700,          // cost/procedure
  perWeekMin = 2, perWeekMax = 9,          // procedures/week
  weeksMin = 50, weeksMax = 53,            // working weeks/year
  priceStep = 25                            // snap to clean steps
}){
  const secRef = useRef(null);
  const progress = easeInOutCubic(useSectionScrollProgress(secRef));

  // derive scrolling values
  const priceRaw = lerp(priceMin, priceMax, progress);
  const price = Math.round(priceRaw / priceStep) * priceStep;

  const perWeekRaw = lerp(perWeekMin, perWeekMax, progress);
  const perWeek = Math.round(perWeekRaw);

  const weeks = Math.round(lerp(weeksMin, weeksMax, progress));

  const weekly = price * perWeek;
  const yearly = weekly * weeks;

  return (
    <section className="earnings-scroll-wrap" ref={secRef} aria-label="Earnings potential (scroll)">
      <div className="earnings-scroll-inner">
        <div className="es-head">
          <div className="es-kicker">Potential to earn</div>
          <div className="es-title">six figures +</div>
        </div>

        <div className="es-block">
          <div className="es-number">{`$${fmtUSD(price)}`}</div>
          <div className="es-label">COST PER PROCEDURE</div>
        </div>

        <div className="es-block">
          <div className="es-number">{`$${fmtUSD(weekly)}`}</div>
          <div className="es-label">WEEKLY INCOME BASED ON {perWeek} PROCEDURES</div>
        </div>

        <div className="es-block">
          <div className="es-number">{`$${fmtUSD(yearly)}`}</div>
          <div className="es-label">YEARLY INCOME</div>
        </div>
      </div>

      <div className="es-wash" aria-hidden="true" />

      {/* Disclaimer */}
      <div className="earnings-note c">
        Estimates shown are for illustration; actual results vary by market, experience, and determination.
      </div>
    </section>
  );
}

/* ===================== Page ===================== */
// …imports stay the same…

export default function LandingHero() {
  const heroRef = useRef(null);
  const [heroDim, setHeroDim] = useState(0);
  const heroHeadline = "Welcome to The World of Permanent Cosmetics";

  useEffect(() => {
    function handleScroll() {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      const vh = window.innerHeight;
      const raw = (0 - rect.top) / vh;
      const clamped = Math.min(Math.max(raw, 0), 1);
      setHeroDim(clamped);
    }
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  return (
    <div className="full-landing">
      <Helmet>{/* …(unchanged head/meta) … */}</Helmet>

      {/* ===================== HERO ===================== */}
      <section className="hero-shell" aria-label="COSMETINK Academy Hero" ref={heroRef}>

<div className="hero-bottom">
  <div className="scroll-cue outline-text">
    <div className="scroll-circle">
      <div className="scroll-dot" />
    </div>
    <div className="scroll-text">Scroll</div>
  </div>
</div>
        <div className="hero-media">
          <video className="hero-video" playsInline autoPlay muted loop preload="auto"
            poster="https://cosmetinklanding.s3.us-east-2.amazonaws.com/academy-link.jpg">
            <source src="https://cosacademyassets.s3.us-east-2.amazonaws.com/AcademyLandingMobile.mp4" media="(max-width: 800px)" type="video/mp4" />
            <source src="https://cosacademyassets.s3.us-east-2.amazonaws.com/AcademyLanding2.mp4" media="(min-width: 801px)" type="video/mp4" />
          </video>

          <div className="hero-overlay" />
          <div className="hero-vignette" />
          <div className="hero-dimmer-layer" style={{ opacity: lerp(0, 1, heroDim) }} />
        </div>

        {/* content */}
        <div className="hero-inner layout-bottom-left">
          <div className="hero-block">
            {/* Headline — opacity only */}
            <div className="hero-invite fade-in-only">
              <span className="headline-base">{heroHeadline}</span>
              <span
                className="headline-gloss"
                aria-hidden="true"
                data-text={heroHeadline}
              />
             
            </div>

            {/* Accreditation card — opacity only */}
            <div className="cred-wrapper fade-in-only">
              <div className="accredit-card glass-rise">
                <div className="accredit-top">
                  <div className="accredit-badge">
                    <img
                      className="accredit-logo"
                      src="https://cosacademyassets.s3.us-east-2.amazonaws.com/IMG_4959.PNG"
                      alt="AAM Accredited"
                    />
                    <div className="accredit-copy">
                      <div className="acc-top">Accredited Permanent Makeup Training & Apprenticeships</div>
                      <div className="acc-sub">American Academy of Micropigmentation</div>
                      <div className="acc-credline">East Coast’s Premier Accredited Academy</div>
                    </div>
       
                  </div>
                </div>

                <div className="accredit-info">
                 {/* Location block (with tiny inline icon) */}
<div className="info-col">
  <div className="info-head">Location</div>
  <div className="info-body info-body--with-icon">

    ⚲ Lehigh Valley, Pennsylvania
  </div>
</div>

{/* Decorative stamp in the lower-right of the glass card */}
<img className="machine-stamp" src={MACHINE_ICON} alt="" aria-hidden="true" />
</div></div>
              {/* Scroll cue (optional) */}
            
            </div>
          </div>
        </div>
      </section>

{/* === “No Experience / No License” banner === */}
<section className="qualify-banner fade-in-only" aria-label="Requirements">
  <div className="qb-inner">
    <div className="qb-item">
      <span className="no-pill">NO</span>
      <span className="qb-text">Experience Needed</span>
    </div>

    <span className="qb-dot" aria-hidden="true">•</span>

    <div className="qb-item">
      <span className="no-pill">NO</span>
      <span className="qb-text">Esthetician License Required</span>
    </div>

    <span className="qb-dot qb-hide-xs" aria-hidden="true">•</span>

    <div className="qb-item">
      <span className="qb-text qb-accent">Start here — get COSMETINK®-certified</span>
    </div>

    
  </div>

</section>


      {/* ===================== BELOW FIRST FOLD ===================== */}
      <EarningsScrollSection
        priceMin={300}
        priceMax={700}
        perWeekMin={2}
        perWeekMax={9}
        weeksMin={50}
        weeksMax={53}
        priceStep={25}
      />
      <AcademyShowcase />
      <InkapprenticeTV />
      <AcademyFeatures />
      <AcademyCommunitySection />
      <ReviewsSection />
    </div>
  );
}
